import api from '../utils/api';

export type DailyCareSubItem = {
  id: number;
  categoryId: number;
  name: string;
  displayOrder: number;
  itemType: 'task' | 'observation' | 'yes_no' | 'dropdown' | 'checkbox' | 'short_text';
  allowedValues: string[] | null;
  isActive: boolean;
};

export type ResidentCarePlanItem = {
  id: number;
  residentId: number;
  subItemId: number;
  categoryId: number;
  supportNote: string | null;
  frequency: string;
  isActive: boolean;
  subItem: DailyCareSubItem;
  category: { id: number; slug: string; name: string; displayOrder: number };
  assignedBy?: { id: number; name: string } | null;
};

export type CarePlanItemInput = {
  subItemId: number;
  supportNote?: string | null;
  frequency?: string;
};

export type DailyCareCategory = {
  id: number;
  slug: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  subItems: DailyCareSubItem[];
};

export type DailyCareLogEntry = {
  id: number;
  logId?: number;
  categoryId: number;
  subItemIds: number[];
  notes: string | null;
  extraData: Record<string, unknown>;
  category?: { id: number; slug: string; name: string };
  selectedSubItems?: DailyCareSubItem[];
};

export type DailyCareLogListRow = {
  id: number;
  residentId: number;
  carerId: number;
  logDate: string;
  logTime: string;
  generalNotes: string | null;
  photoKeys: string[];
  residentName: string | null;
  carerName: string | null;
  concernFlag: boolean;
};

export type DailyCareLogDetail = {
  id: number;
  residentId: number;
  carerId: number;
  logDate: string;
  logTime: string;
  generalNotes: string | null;
  handoverNote: string | null;
  concernFlag: boolean;
  concernNote: string | null;
  editedAt: string | null;
  editedBy: { id: number; name: string } | null;
  photoKeys: string[];
  resident: { id: number; name: string } | null;
  carer: { id: number; name: string; email?: string } | null;
  entries: DailyCareLogEntry[];
  photos: { key: string; url: string | null }[];
};

export type LogFilter = {
  from?: string;
  to?: string;
  residentId?: number;
  carerId?: number;
  concernFlag?: boolean;
  page?: number;
  limit?: number;
};

export type ComplianceRow = {
  carerId: number;
  name: string | null;
  logsSubmitted: number;
  distinctResidents: number;
  distinctDays: number;
};

export type DailySummary = {
  date: string;
  totalLogs: number;
  uniqueResidents: number;
  uniqueCarers: number;
  perCategory: { slug: string; name: string; count: number }[];
};

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  });
  const s = u.toString();
  return s ? `?${s}` : '';
}

export const dailyCareLogsApi = {
  getCategories: async (): Promise<DailyCareCategory[]> => {
    const r = await api.get('/daily-care-logs/categories');
    return r.data.data;
  },

  adminListLogs: async (
    f: LogFilter
  ): Promise<{ data: DailyCareLogListRow[]; pagination: { page: number; limit: number; total: number } }> => {
    const r = await api.get(`/daily-care-logs/admin/logs${qs(f as Record<string, unknown>)}`);
    return { data: r.data.data, pagination: r.data.pagination };
  },

  getLog: async (logId: number): Promise<DailyCareLogDetail> => {
    const r = await api.get(`/daily-care-logs/${logId}`);
    return r.data.data;
  },

  missingLogs: async (date: string): Promise<{ date: string; count: number; data: { id: number; name: string }[] }> => {
    const r = await api.get(`/daily-care-logs/admin/missing-logs${qs({ date })}`);
    return { date: r.data.date, count: r.data.count, data: r.data.data };
  },

  summary: async (date: string): Promise<DailySummary> => {
    const r = await api.get(`/daily-care-logs/admin/summary/${date}`);
    return r.data.data;
  },

  compliance: async (from: string, to: string): Promise<ComplianceRow[]> => {
    const r = await api.get(`/daily-care-logs/admin/compliance${qs({ from, to })}`);
    return r.data.data;
  },

  createSubItem: async (
    categoryId: number,
    payload: { name: string; displayOrder?: number; itemType?: 'task' | 'observation' | 'yes_no' | 'dropdown' | 'checkbox' | 'short_text'; allowedValues?: string[] | null }
  ): Promise<DailyCareSubItem> => {
    const r = await api.post(`/daily-care-logs/admin/categories/${categoryId}/sub-items`, payload);
    return r.data.data;
  },

  updateSubItem: async (
    id: number,
    patch: Partial<Pick<DailyCareSubItem, 'name' | 'displayOrder' | 'isActive' | 'itemType' | 'allowedValues'>>
  ): Promise<DailyCareSubItem> => {
    const r = await api.put(`/daily-care-logs/admin/sub-items/${id}`, patch);
    return r.data.data;
  },

  deleteSubItem: async (id: number): Promise<void> => {
    await api.delete(`/daily-care-logs/admin/sub-items/${id}`);
  },

  createCategory: async (slug: string, name: string, displayOrder = 0): Promise<DailyCareCategory> => {
    const r = await api.post('/daily-care-logs/admin/categories', { slug, name, displayOrder });
    return r.data.data;
  },

  updateCategory: async (
    id: number,
    patch: Partial<Pick<DailyCareCategory, 'name' | 'displayOrder' | 'isActive'>>
  ): Promise<DailyCareCategory> => {
    const r = await api.put(`/daily-care-logs/admin/categories/${id}`, patch);
    return r.data.data;
  },

  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/daily-care-logs/admin/categories/${id}`);
  },

  exportPdfUrl: (f: LogFilter): string => {
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    return `${base}/daily-care-logs/admin/logs/export-pdf${qs(f as Record<string, unknown>)}`;
  },

  updateLog: async (
    logId: number,
    patch: { generalNotes?: string; handoverNote?: string; concernFlag?: boolean; concernNote?: string }
  ): Promise<DailyCareLogDetail> => {
    const r = await api.put(`/daily-care-logs/${logId}`, patch);
    return r.data.data;
  },

  exportLogPdfUrl: (logId: number): string => {
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    return `${base}/daily-care-logs/${logId}/pdf`;
  }
};

export const residentCarePlanApi = {
  list: async (residentId: number): Promise<ResidentCarePlanItem[]> => {
    const r = await api.get(`/daily-care/residents/${residentId}/care-plan-items`);
    return r.data.data;
  },
  saveBulk: async (residentId: number, items: CarePlanItemInput[]): Promise<ResidentCarePlanItem[]> => {
    const r = await api.post(`/daily-care/residents/${residentId}/care-plan-items`, { items });
    return r.data.data;
  },
  update: async (id: number, patch: Partial<{ supportNote: string; frequency: string; isActive: boolean }>): Promise<ResidentCarePlanItem> => {
    const r = await api.put(`/daily-care/care-plan-items/${id}`, patch);
    return r.data.data;
  },
  remove: async (id: number): Promise<void> => {
    await api.delete(`/daily-care/care-plan-items/${id}`);
  }
};
